import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { adminApi } from '../../services/admin';
import type { AdminUploadItem, AdminEmailIngestion } from '../../types';

// --- Uploads helpers ---

const uploadStatusVariant = (s: string) =>
  s === 'completed' ? 'success' : s === 'processing' ? 'warning' : s === 'failed' ? 'error' : 'neutral';

const uploadColumns: Column<AdminUploadItem>[] = [
  { key: 'file_name', header: 'File' },
  { key: 'file_type', header: 'Type', render: (r) => r.file_type.toUpperCase() },
  { key: 'source_type', header: 'Source', render: (r) => (
    <span className="capitalize">{r.source_type.replaceAll('_', ' ')}</span>
  )},
  { key: 'vendor_org_name', header: 'Vendor' },
  { key: 'site_name', header: 'Site' },
  { key: 'status', header: 'Status', render: (r) => (
    <StatusBadge label={r.status} variant={uploadStatusVariant(r.status)} />
  )},
  { key: 'events_parsed_count', header: 'Events' },
  { key: 'file_size', header: 'Size', render: (r) => {
    if (r.file_size >= 1024 * 1024) return `${(r.file_size / (1024 * 1024)).toFixed(1)} MB`;
    if (r.file_size >= 1024) return `${(r.file_size / 1024).toFixed(0)} KB`;
    return `${r.file_size} B`;
  }},
  { key: 'created_at', header: 'Uploaded', render: (r) =>
    new Date(r.created_at).toLocaleString()
  },
  {
    key: 'download_url',
    header: 'File',
    render: (r) => r.download_url ? (
      <a href={r.download_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 text-xs font-medium">
        Download
      </a>
    ) : <span className="text-xs text-slate-400">&mdash;</span>,
  },
];

// --- Email Ingestion helpers ---

const emailStatusVariant = (s: string): 'success' | 'warning' | 'error' | 'neutral' =>
  s === 'completed' ? 'success' : s === 'processing' ? 'warning' : s === 'failed' || s === 'rejected' ? 'error' : 'neutral';

const authVariant = (s: string): 'success' | 'warning' | 'error' | 'neutral' =>
  s === 'authorized' ? 'success' : s === 'rejected' ? 'error' : s === 'pending' ? 'warning' : 'neutral';

const emailColumns: Column<AdminEmailIngestion>[] = [
  { key: 'sender_email', header: 'Sender' },
  { key: 'site_name', header: 'Site', render: (r) => r.site_name ?? 'Unknown' },
  { key: 'subject', header: 'Subject', render: (r) => {
    const subj = r.subject ?? '';
    return subj.length > 50 ? `${subj.slice(0, 50)}...` : subj;
  }},
  { key: 'status', header: 'Status', render: (r) => (
    <StatusBadge label={r.status} variant={emailStatusVariant(r.status)} />
  )},
  { key: 'authorization_status', header: 'Auth', render: (r) => (
    <StatusBadge label={r.authorization_status} variant={authVariant(r.authorization_status)} />
  )},
  { key: 'attachment_count', header: 'Attachments' },
  { key: 'upload_count', header: 'Uploads' },
  { key: 'received_at', header: 'Received', render: (r) =>
    new Date(r.received_at).toLocaleString()
  },
];

// --- Tabs ---

type Tab = 'uploads' | 'emails';

export default function AdminIngestion() {
  const [activeTab, setActiveTab] = useState<Tab>('uploads');

  // Uploads state
  const [uploadsPage, setUploadsPage] = useState(1);
  const [uploadsSearch, setUploadsSearch] = useState('');
  const [uploadsStatus, setUploadsStatus] = useState('');

  // Email ingestions state
  const [emailsPage, setEmailsPage] = useState(1);
  const [emailsSearch, setEmailsSearch] = useState('');
  const [emailsStatus, setEmailsStatus] = useState('');
  const [emailsAuth, setEmailsAuth] = useState('');

  const queryClient = useQueryClient();

  // Uploads query
  const { data: uploadsData, isLoading: uploadsLoading } = useQuery({
    queryKey: ['admin', 'uploads', uploadsPage, uploadsSearch, uploadsStatus],
    queryFn: () => adminApi.getUploads({
      page: String(uploadsPage),
      search: uploadsSearch || undefined,
      status: uploadsStatus || undefined,
    }),
  });

  // Email ingestions query
  const { data: emailsData, isLoading: emailsLoading } = useQuery({
    queryKey: ['admin', 'emailIngestions', emailsPage, emailsSearch, emailsStatus, emailsAuth],
    queryFn: () => adminApi.getEmailIngestions({
      page: String(emailsPage),
      search: emailsSearch || undefined,
      status: emailsStatus || undefined,
      authorization_status: emailsAuth || undefined,
    }),
  });

  const reprocessMutation = useMutation({
    mutationFn: (id: string) => adminApi.reprocessEmailIngestion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'emailIngestions'] });
    },
  });

  const uploadsTotalPages = uploadsData ? Math.ceil(uploadsData.count / 20) : 1;
  const emailsTotalPages = emailsData ? Math.ceil(emailsData.count / 20) : 1;

  const description = activeTab === 'uploads'
    ? `${uploadsData?.count ?? 0} uploads`
    : `${emailsData?.count ?? 0} email ingestions`;

  return (
    <>
      <PageHeader title="Ingestion" description={description} />

      {/* Tab toggles */}
      <div className="mb-6 flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('uploads')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'uploads'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Uploads
        </button>
        <button
          onClick={() => setActiveTab('emails')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'emails'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Email Ingestions
        </button>
      </div>

      {/* Uploads tab */}
      {activeTab === 'uploads' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              type="text"
              placeholder="Search uploads..."
              value={uploadsSearch}
              onChange={(e) => { setUploadsSearch(e.target.value); setUploadsPage(1); }}
              className="w-full sm:w-80 rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <select
              value={uploadsStatus}
              onChange={(e) => { setUploadsStatus(e.target.value); setUploadsPage(1); }}
              className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
              <option value="partial">Partial</option>
            </select>
          </div>
          <DataTable
            columns={uploadColumns}
            data={uploadsData?.results ?? []}
            keyExtractor={(r) => r.id}
            isLoading={uploadsLoading}
            emptyTitle="No uploads"
            emptyDescription="Uploads appear when vendors submit DARs."
            page={uploadsPage}
            totalPages={uploadsTotalPages}
            onPageChange={setUploadsPage}
          />
        </>
      )}

      {/* Email Ingestions tab */}
      {activeTab === 'emails' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              type="text"
              placeholder="Search email ingestions..."
              value={emailsSearch}
              onChange={(e) => { setEmailsSearch(e.target.value); setEmailsPage(1); }}
              className="w-full sm:w-80 rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <select
              value={emailsStatus}
              onChange={(e) => { setEmailsStatus(e.target.value); setEmailsPage(1); }}
              className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="received">Received</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={emailsAuth}
              onChange={(e) => { setEmailsAuth(e.target.value); setEmailsPage(1); }}
              className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">All Authorization</option>
              <option value="authorized">Authorized</option>
              <option value="rejected">Rejected</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <DataTable
            columns={emailColumns}
            data={emailsData?.results ?? []}
            keyExtractor={(r) => r.id}
            isLoading={emailsLoading}
            emptyTitle="No email ingestions"
            emptyDescription="Email ingestions appear when the system receives DARs via email."
            page={emailsPage}
            totalPages={emailsTotalPages}
            onPageChange={setEmailsPage}
          />
        </>
      )}
    </>
  );
}
